import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Calendar, Crown, Eye, Home, User, UserCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { authClient } from '@/lib/auth-client';
import { trpcClient } from '@/utils/trpc';

interface InviteSearch {
  token?: string;
}

export const Route = createFileRoute('/invite')({
  validateSearch: (search): InviteSearch => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { token } = Route.useSearch();
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const navigate = useNavigate();

  const [inviteDetails, setInviteDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  // Load invite details
  useEffect(() => {
    if (!token) {
      setError('Token de convite não fornecido');
      setLoading(false);
      return;
    }

    trpcClient.household.getInviteDetails
      .query({ token })
      .then((details) => {
        setInviteDetails(details);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Erro ao carregar convite');
        setLoading(false);
      });
  }, [token]);

  // Accept invite if user is logged in
  const handleAcceptInvite = async () => {
    if (!token || !session) return;

    setAccepting(true);
    try {
      const result = await trpcClient.household.acceptInvite.mutate({ token });

      if (result.success) {
        // Redirect to dashboard after accepting
        navigate({ to: '/dashboard' });
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao aceitar convite');
    } finally {
      setAccepting(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Crown className="h-4 w-4" />;
      case 'member':
        return <UserCheck className="h-4 w-4" />;
      case 'viewer':
        return <Eye className="h-4 w-4" />;
      default:
        return <UserCheck className="h-4 w-4" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'destructive';
      case 'member':
        return 'default';
      case 'viewer':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getRoleText = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'member':
        return 'Membro';
      case 'viewer':
        return 'Visualizador';
      default:
        return 'Membro';
    }
  };

  if (sessionPending || loading) {
    return (
      <div className="container mx-auto max-w-2xl p-6">
        <div className="space-y-6">
          <div className="h-8 animate-pulse rounded bg-muted" />
          <div className="h-64 animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (error || !inviteDetails) {
    return (
      <div className="container mx-auto max-w-2xl p-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-red-600">Convite Inválido</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-muted-foreground">
              {error || 'Convite não encontrado ou expirado.'}
            </p>
            <Button
              onClick={() => navigate({ to: '/login' })}
              variant="outline"
            >
              Fazer Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-2xl p-6">
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="mb-2 font-bold text-3xl tracking-tight">
            💰 Control Money
          </h1>
          <p className="text-muted-foreground">
            Você foi convidado para uma casa financeira!
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Home className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>
                  Convite para {inviteDetails.householdName}
                </CardTitle>
                <CardDescription>
                  Convidado por {inviteDetails.inviterName}
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="grid gap-4">
              <div className="flex items-center justify-between rounded-lg bg-muted p-3">
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4" />
                  <span className="font-medium">Seu papel:</span>
                </div>
                <Badge
                  className="gap-1"
                  variant={getRoleColor(inviteDetails.role) as any}
                >
                  {getRoleIcon(inviteDetails.role)}
                  {getRoleText(inviteDetails.role)}
                </Badge>
              </div>

              <div className="flex items-center justify-between rounded-lg bg-muted p-3">
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4" />
                  <span className="font-medium">Expira em:</span>
                </div>
                <span className="text-muted-foreground text-sm">
                  {new Date(inviteDetails.expiresAt).toLocaleDateString(
                    'pt-BR'
                  )}
                </span>
              </div>
            </div>

            <div className="rounded-lg border border-blue-500/20 bg-blue-500/10 p-4">
              <h4 className="mb-2 font-semibold text-blue-700 dark:text-blue-300">
                O que você poderá fazer:
              </h4>
              <ul className="space-y-1 text-blue-600 text-sm dark:text-blue-400">
                <li>📊 Criar e acompanhar orçamentos em família</li>
                <li>💸 Registrar gastos e receitas</li>
                <li>🎯 Definir metas financeiras conjuntas</li>
                <li>💬 Receber dicas financeiras via WhatsApp</li>
              </ul>
            </div>

            {session ? (
              <div className="space-y-4">
                <div className="text-center">
                  <p className="mb-4 text-muted-foreground text-sm">
                    Logado como <strong>{session.user.email}</strong>
                  </p>
                  {session.user.email === inviteDetails.email ? (
                    <Button
                      className="w-full"
                      disabled={accepting}
                      onClick={handleAcceptInvite}
                    >
                      {accepting ? 'Aceitando...' : 'Aceitar Convite'}
                    </Button>
                  ) : (
                    <div className="space-y-4">
                      <p className="rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3 text-sm text-yellow-700 dark:text-yellow-300">
                        ⚠️ Este convite é para{' '}
                        <strong>{inviteDetails.email}</strong>, mas você está
                        logado como <strong>{session.user.email}</strong>.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          className="flex-1"
                          onClick={() =>
                            authClient.signOut({
                              fetchOptions: {
                                onSuccess: () => window.location.reload(),
                              },
                            })
                          }
                          variant="outline"
                        >
                          Fazer Logout
                        </Button>
                        <Button
                          className="flex-1"
                          onClick={() => navigate({ to: '/dashboard' })}
                          variant="outline"
                        >
                          Ir para Dashboard
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-center text-muted-foreground text-sm">
                  Você precisa fazer login como{' '}
                  <strong>{inviteDetails.email}</strong> para aceitar este
                  convite.
                </p>
                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    onClick={() =>
                      navigate({
                        to: '/login',
                        search: {
                          redirect: `/invite?token=${token}`,
                          mode: 'login',
                          email: inviteDetails.email,
                        },
                      })
                    }
                  >
                    Fazer Login
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={() =>
                      navigate({
                        to: '/login',
                        search: {
                          redirect: `/invite?token=${token}`,
                          mode: 'signup',
                          email: inviteDetails.email,
                        },
                      })
                    }
                    variant="outline"
                  >
                    Criar Conta
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
